/**
 * Currency formatter utility
 * Formats monetary amounts according to their currency and locale conventions
 */

export interface MoneyAmount {
  amount: number;
  currency: string;
  decimals: number;
}

/**
 * Currency configuration for different locales
 */
const CURRENCY_CONFIG: Record<string, { locale: string; symbol?: string }> = {
  EUR: { locale: 'es-ES' }, // 1.000,00 €
  USD: { locale: 'en-US' }, // $1,000.00
  MXN: { locale: 'es-MX' }, // $1,000.00 MXN
  BRL: { locale: 'pt-BR' }, // R$ 1.000,00
  GBP: { locale: 'en-GB' }, // £1,000.00
};

/**
 * Formats a monetary amount according to its currency
 * @param money - Money object with amount (in cents), currency, and decimals
 * @returns Formatted string with currency symbol and proper formatting
 */
export const formatCurrency = (money: MoneyAmount): string => {
  if (!money || money.amount === undefined) {
    return '0,00 €';
  }

  const { amount, currency, decimals = 2 } = money;
  
  // Convert from cents to actual amount
  const actualAmount = amount / Math.pow(10, decimals);
  
  // Get currency configuration or default to EUR
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.EUR;
  
  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true, // Always use thousand separators
    });
    
    let result = formatter.format(actualAmount);
    
    // For MXN, manually add the currency code after the symbol
    if (currency === 'MXN') {
      result = result.replace('MX', '').trim() + ' MXN';
    }
    
    return result;
  } catch (error) {
    // Fallback if currency is not supported
    console.warn(`Currency ${currency} not supported, using EUR as fallback`);
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    }).format(actualAmount);
  }
};

/**
 * Formats a monetary amount without currency symbol
 * @param money - Money object with amount (in cents), currency, and decimals
 * @returns Formatted number string without currency symbol
 */
export const formatAmount = (money: MoneyAmount): string => {
  if (!money || money.amount === undefined) {
    return '0,00';
  }

  const { amount, currency, decimals = 2 } = money;
  
  // Convert from cents to actual amount
  const actualAmount = amount / Math.pow(10, decimals);
  
  // Get currency configuration or default to EUR
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.EUR;
  
  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true, // Always use thousand separators
    });
    
    return formatter.format(actualAmount);
  } catch (error) {
    // Fallback
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    }).format(actualAmount);
  }
};

/**
 * Gets the currency symbol for a given currency code
 * @param currency - Currency code (EUR, USD, etc.)
 * @returns Currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.EUR;
  
  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
    });
    
    // Extract symbol from formatted string
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find(part => part.type === 'currency');
    return symbolPart?.value || currency;
  } catch (error) {
    return currency;
  }
};

/**
 * Formats currency with compact notation for large amounts
 * @param money - Money object
 * @returns Formatted string (e.g., "€1.2K", "€1.5M")
 */
export const formatCurrencyCompact = (money: MoneyAmount): string => {
  if (!money || money.amount === undefined) {
    return '€0';
  }

  const { amount, currency, decimals = 2 } = money;
  
  // Convert from cents to actual amount
  const actualAmount = amount / Math.pow(10, decimals);
  
  // Get currency configuration or default to EUR
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.EUR;
  
  try {
    const formatter = new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: currency,
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    });
    
    return formatter.format(actualAmount);
  } catch (error) {
    // Fallback
    return formatCurrency(money);
  }
};

/**
 * Example usage:
 * 
 * const euroAmount = { amount: 86681, currency: 'EUR', decimals: 2 };
 * formatCurrency(euroAmount); // "866,81 €"
 * 
 * const usdAmount = { amount: 100000, currency: 'USD', decimals: 2 };
 * formatCurrency(usdAmount); // "$1,000.00"
 * 
 * const mxnAmount = { amount: 50000, currency: 'MXN', decimals: 2 };
 * formatCurrency(mxnAmount); // "$500.00 MXN"
 * 
 * const brlAmount = { amount: 250000, currency: 'BRL', decimals: 2 };
 * formatCurrency(brlAmount); // "R$ 2.500,00"
 * 
 * const gbpAmount = { amount: 75000, currency: 'GBP', decimals: 2 };
 * formatCurrency(gbpAmount); // "£750.00"
 */
